import { useHotelBookingContext } from "@/app/lib/context/HotelBookingContext";
import { useOptionalHotelDataContext } from "@/app/lib/context/HotelDataContext";
import { useHotelOffersContext } from "@/app/lib/context/HotelOffersContext";
import { HotelRoomSelection } from "@/app/ui/hotel/HotelRoomSelection";
import { PersistentDrawer } from "@/app/ui/PersistentDrawer";
import { Button, Alert, Dialog, DialogContent } from "@mui/material";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import * as Sentry from "@sentry/react";
import { useHotelSearchDataContext } from "@/app/lib/context/HotelSearchDataContext";
import {
  BnplTrackingEvents,
  HotelTrackingEvents,
  rudderAnalytics,
} from "@/app/lib/rudderstack";
import { useHotelRoomAvailabilityMutation } from "@/app/lib/hooks/useHotelRoomAvailability";
import { useHotelSearch } from "@/app/lib/hooks/useHotelSearch";
import PageLoader from "@/app/ui/PageLoader";
import AnimatedLoader from "@/app/ui/AnimatedLoader";
import { ErrorPanel } from "@/app/ui/ErrorPanel";
import { IconSadFace } from "@/app/icons";
import type { GetHotelAvailabilityTokenOutputData } from "@/app/lib/types/codegen";
import { useState } from "react";
import PaymentChooser from "@/app/ui/PaymentChooser";
import { usePaymentContext } from "@/app/lib/context/PaymentContext";
import { Feature, isFeatureEnabled } from "@/app/lib/utils/featureFlags";
import { PaymentOptions } from "@/app/ui/PaymentOptions";
import { Drawer, DrawerTopBar } from "@/app/ui/Drawer";
import { isOfferIsNotAvailableAnymoreError } from "@/app/lib/assertions";

export const Route = createFileRoute(
  "/_authenticated/_hotels/_hotel-offer/hotels/$id/rooms",
)({
  component: RouteComponent,
  staticData: {
    hotelStep: { stepNumber: 1 },
    drawer: true,
  },
});

function RouteComponent() {
  const t = useTranslations();
  const navigate = Route.useNavigate();
  const { id } = Route.useParams();
  const params = Route.useParams();

  const { data: offers } = useHotelOffersContext();
  const { data: hotelData } = useOptionalHotelDataContext();
  const { searchResultKey, searchResultToken } = useHotelSearch();
  const { data: booking, setAvailabilityToken } = useHotelBookingContext();
  const { searchData } = useHotelSearchDataContext();

  const [showPaymentChooser, setShowPaymentChooser] = useState(false);
  const { isEligibilityLoading, paymentType, isEligible } = usePaymentContext();

  const isBnplFeatureEnabled =
    isFeatureEnabled(Feature.Bnpl) && Boolean(isEligible);

  const [paymentError, setPaymentError] = useState<string | null>(null);

  const selectedRoomPrice = booking?.price?.value || 0;
  const fallbackPrice = hotelData?.minPrice?.value || 0;
  const originalPrice = selectedRoomPrice || fallbackPrice;

  const {
    mutateAsync: getRoomAvailability,
    isPending: isRoomAvailabilityPending,
    error: roomAvailabilityError,
  } = useHotelRoomAvailabilityMutation({
    onSuccess: (data: GetHotelAvailabilityTokenOutputData) => {
      setAvailabilityToken(data.availabilityToken);
    },
  });

  const handleContinue = async () => {
    setPaymentError(null);

    const eventName =
      paymentType === "bnpl"
        ? BnplTrackingEvents.USER_SELECTED_BNPL_PAYMENT
        : BnplTrackingEvents.USER_SELECTED_STANDARD_PAYMENT;

    rudderAnalytics.track(eventName, {
      is_bnpl_eligible: isEligible,
    });

    const currentOffer = offers?.find(
      (offer) =>
        offer.offerId === booking?.offerId &&
        offer.packageToken === booking?.packageToken,
    );

    if (!booking || !currentOffer || !searchResultKey || !searchResultToken) {
      setPaymentError("Cannot check room availability, params missing");
      return;
    }

    await getRoomAvailability({
      hotelId: id,
      searchResultKey,
      searchResultToken,
      packageToken: currentOffer.packageToken,
      offerId: booking.offerId,
      roomTokens: booking.rooms.map((room) => room.roomToken),
    });

    rudderAnalytics.track(
      HotelTrackingEvents.USER_PROCEED_AFTER_ROOM_SELECTION,
      {
        bookingType: "Hotel",
        selected_offer_id: booking?.offerId,
        price: booking?.price?.value,
      },
    );
    navigate({ to: "/hotels/$id/travelers" });
  };

  if (!searchData || !hotelData) {
    Sentry.captureMessage(
      "User tried to access hotel rooms page directly without going through the hotel offer page",
      "fatal",
    );
    return <Navigate to="/hotels/$id/details" params={params} />;
  }

  return (
    <>
      <HotelRoomSelection
        offers={offers}
        onRoomGalleryClick={(option) => {
          navigate({
            to: "./$roomToken/images",
            params: { roomToken: option.roomToken },
          });
        }}
      />

      <Dialog
        open={
          isRoomAvailabilityPending ||
          isOfferIsNotAvailableAnymoreError(roomAvailabilityError)
        }
        fullWidth
        maxWidth="xs"
      >
        <DialogContent>
          {isRoomAvailabilityPending ? (
            <PageLoader>
              <AnimatedLoader icon="buildings" />
              <h3 className="mt-3 font-normal text-text-primary">
                {t("hotels.checkingRoomAvailability")}
              </h3>
            </PageLoader>
          ) : (
            <ErrorPanel
              icon={IconSadFace}
              primaryText={t("hotels.notAvailable.title")}
              secondaryText={t("hotels.notAvailable.description")}
            >
              <Button
                LinkComponent={Link}
                variant="outlined"
                color="primary"
                size="small"
                href="/hotels/list"
                sx={{ borderRadius: "var(--radius-2xl)" }}
              >
                {t("hotels.notAvailable.button")}
              </Button>
            </ErrorPanel>
          )}
        </DialogContent>
      </Dialog>

      <PersistentDrawer>
        <PaymentOptions totalPrice={originalPrice} />

        <Button
          fullWidth
          disabled={
            !booking ||
            searchData?.rooms.length !== booking?.rooms.length ||
            isEligibilityLoading
          }
          variant="contained"
          onClick={() => {
            if (isBnplFeatureEnabled) {
              setShowPaymentChooser(true);
            } else {
              handleContinue();
            }
          }}
        >
          {t("hotels.search.bookNow")}
        </Button>
      </PersistentDrawer>

      <Drawer
        open={showPaymentChooser}
        onClose={() => setShowPaymentChooser(false)}
      >
        <DrawerTopBar
          onCloseClick={() => setShowPaymentChooser(false)}
          title={t("payment.paymentTypeHeader")}
        />

        <PaymentChooser price={originalPrice} />
        {paymentError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {paymentError}
          </Alert>
        )}

        <Button
          fullWidth
          disabled={
            !booking || searchData?.rooms.length !== booking?.rooms.length
          }
          variant="contained"
          onClick={handleContinue}
        >
          {t("hotels.search.bookNow")}
        </Button>
      </Drawer>
    </>
  );
}
